import React, { useCallback } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import GetAppIcon from '@material-ui/icons/GetApp';
import { SchemaStatuses } from 'constants/schemaStatuses';
import { SummarizeWithAIDrawer, useReferenceCheckSummarization } from 'features/ai-generators';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { getToken } from 'services/Authentication';
import strings from 'strings';
import { FPActionButton, FPIconButton, When } from 'UI/components/atoms';
import FPFormStatus from 'UI/components/molecules/FPFormStatus';
import DrawerContentLayout from 'UI/components/templates/DrawerContentLayout';
import { EnvApiUrls } from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SvgMakeAi } from 'UI/res';

import useReferenceCheckForm from './hooks/useReferenceCheckForm';
import { StyledForm } from './ReferenceCheckDrawer.styled';
import { DrawerMode } from './ReferenceCheckTabLayout.constants';
import { validateReferenceCheck } from './utils';

const MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY =
  window.GPAC_ENV?.MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY ??
  process.env.REACT_APP_MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY;

const MIN_REQUIRED_FIELDS = MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY
  ? Number(MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY)
  : 0;

const BASE_URL = `${EnvApiUrls.API_URL_AWS}/`;
const COPIES = strings.inventoryProfiles.sections.tabs.referenceCheck;

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {boolean} props.isSaving
 * @param {boolean} props.isSaved
 * @param {string} [props.status]
 * @param {number} props.candidateId
 * @param {string} [props.candidateName]
 * @param {string} [props.recruiterName]
 * @param {number} [props.referenceId]
 * @param {'create'|'edit'|'view'} [props.mode]
 * @param {Function} props.onClose
 * @param {Function} [props.onEditItemClick]
 * @param {Function} props.onSave
 * @param {Function} props.onSaveSuccess
 * @param {Function} props.onSaveError
 */
const ReferenceCheckDrawer = ({
  isOpen,
  isSaving,
  isSaved,
  status,
  candidateId,
  candidateName,
  recruiterName,
  referenceId,
  mode,
  onClose,
  onEditItemClick,
  onSave,
  onSaveSuccess,
  onSaveError
}) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const canDownload = checkIfFeatureFlagEnabled(FeatureFlags.ReferenceCheckDownload);
  const showDownloadButton = canDownload && mode === DrawerMode.View;

  const [formState, dispatch] = useReferenceCheckForm({
    referenceId,
    candidateId,
    candidateName: candidateName || '',
    recruiterName: recruiterName || '',
    mode,
    onSaveSuccess,
    onSave,
    onSaveError
  });

  const {
    aiSummaryState,
    closeSummarizedDrawer,
    handleClickSummarizeWithAI,
    isSummaryWithAiEnabled,
    results
  } = useReferenceCheckSummarization();

  const hasEnoughInfoToSummarize =
    formState.reference?.reference?.progress?.key === SchemaStatuses.Completed ||
    formState.reference?.reference?.progress?.current > MIN_REQUIRED_FIELDS;

  const onCloseModal = useCallback(() => {
    onClose();
    dispatch.handleResetForm();
  }, [dispatch, onClose]);

  const httpOptions = {
    headers: {
      Authorization: `Bearer ${getToken()}`,
      ...(EnvApiUrls.SEND_API_KEY && { 'x-api-key': EnvApiUrls.API_KEY_AWS })
    }
  };

  const commonStyledFormProps = {
    showErrorList: false,
    baseUrl: BASE_URL,
    noHtml5Validate: true,
    httpOptions,
    submitRef: formState.saveButtonRef,
    formData: formState.formData,
    placeholders: formState.placeHolders
  };

  const title = candidateName
    ? strings.formatString(COPIES.title, { candidateName })
    : COPIES.sectionName;
  const subheader = formState.referenceName ? (
    <Box display="flex" alignItems="center" gridGap={8}>
      <Typography variant="body2" color="textSecondary">
        {`${formState.referenceName} / ${
          formState?.formData?.section1?.secondRow?.relationship || ''
        }`}
      </Typography>
      <FPFormStatus
        status={mode !== DrawerMode.Create && mode ? status : SchemaStatuses.Pending}
        shouldShowTotal={false}
      />
    </Box>
  ) : null;

  return (
    <>
      <DrawerContentLayout
        containerComponent="div"
        drawerProps={{
          open: isOpen,
          onClose: onCloseModal
        }}
        primaryProps={{
          type: 'button',
          onClick: () => {
            mode === DrawerMode.View && onEditItemClick
              ? onEditItemClick()
              : formState.saveButtonRef.current.click();
          },
          isSaving,
          isSuccess: isSaved && mode === DrawerMode.Create,
          disabled:
            isSaving ||
            formState.isFetching ||
            (mode === DrawerMode.View && !onEditItemClick) ||
            (isSaved && mode === DrawerMode.Create),
          initialText: mode === DrawerMode.View ? strings.shared.ui.edit : strings.shared.ui.save
        }}
        uiState={{
          isFormLoading: formState.isFetching
        }}
        isTopToolbarNeeded
        title={title}
        subheader={subheader}
        onClose={onCloseModal}
        additionalHeaderButtons={
          showDownloadButton ? (
            <>
              {formState.isDownloading ? (
                <CircularProgress size={24} />
              ) : (
                <FPIconButton
                  onClick={dispatch.handleClickDownload}
                  icon={GetAppIcon}
                  tooltipProps={{ title: strings.shared.ui.download }}
                />
              )}
            </>
          ) : null
        }
      >
        {mode === DrawerMode.Create && (
          <StyledForm
            {...commonStyledFormProps}
            schema={formState.fetchedSchemas?.structure}
            uiSchema={formState.fetchedSchemas?.ui}
            onChange={dispatch.handleFormChange}
            customValidate={validateReferenceCheck}
            onSubmit={dispatch.handleSubmit}
          />
        )}
        {mode === DrawerMode.Edit && (
          <StyledForm
            {...commonStyledFormProps}
            schema={formState.fetchedSchemas?.structure}
            uiSchema={formState.fetchedSchemas?.ui}
            onChange={dispatch.handleFormChange}
            customValidate={validateReferenceCheck}
            noHTML5Validate
            onSubmit={dispatch.handleUpdate}
          />
        )}
        {mode === DrawerMode.View && (
          <>
            <When condition={isSummaryWithAiEnabled}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" gridGap={16}>
                <When condition={aiSummaryState.isLoading}>
                  <CircularProgress size={20} />
                </When>
                <FPActionButton
                  color="primary"
                  disabled={aiSummaryState.isLoading || !hasEnoughInfoToSummarize}
                  icon={SvgMakeAi}
                  onClick={() => handleClickSummarizeWithAI(referenceId)}
                  text={
                    aiSummaryState.isLoading
                      ? COPIES.aiSummary.actionButtonLoading
                      : COPIES.aiSummary.actionButton
                  }
                  variant="outlined"
                  withGradient
                />
              </Box>
            </When>
            <StyledForm
              {...commonStyledFormProps}
              schema={formState.fetchedSchemas?.structure}
              uiSchema={formState.fetchedSchemas?.ui}
              readonly
            />
          </>
        )}
      </DrawerContentLayout>

      <When condition={isSummaryWithAiEnabled}>
        <SummarizeWithAIDrawer
          onClose={closeSummarizedDrawer}
          open={aiSummaryState.openDrawer}
          results={results}
          subheader={subheader}
          title={title}
        />
      </When>
    </>
  );
};

export default ReferenceCheckDrawer;
